// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { StyledButton } from "@insite/mobius/Clickable";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@keller/App/Store/Reducers";
import { constants } from "@keller/XDL/constants";
import { color, layoutContainer, xScreen } from "@keller/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

export interface FooterStyles {
    page?: MobiusPageProps;
    widgetGridContainer?: InjectableCss;
    infoSection?: InjectableCss;
    itemsSection?: InjectableCss;
    logoSection?: InjectableCss;
    copyrightWrapper?: InjectableCss;
}

export const footerStyles: FooterStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
        css: css`
            background: linear-gradient(to left, ${color("common.ui16")} 60%, ${color("common.ui8")} 60%);
            margin-top: ${constants.footer.offsetTop}px;
            ${xScreen("lte", "sm")} {
                background: transparent;
            }
        `,
    },
    widgetGridContainer: {
        css: css`
            display: flex;
            ${layoutContainer}
        `,
    },
    infoSection: {
        css: css`
            padding-top: 40px;
            padding-bottom: 20px;
            background: ${color("common.ui8")};
            max-width: 412px;
            padding-right: 32px;
            display: flex;
            flex-direction: column;
            ${xScreen("lte", "sm")} {
                ${layoutContainer}
            }
        `,
    },
    itemsSection: {
        css: css`
            padding-top: 40px;
            padding-bottom: 20px;
            background: ${color("common.ui16")};
            flex-grow: 1;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
            flex-wrap: wrap;
            padding-left: 62px;
            & > div:not(:last-child) {
                width: 140px;
                height: auto;
                margin-bottom: 20px;
            }
            & > div:last-child {
                flex-basis: 100%;
                height: auto;
            }
            ${xScreen("lte", "sm")} {
                ${layoutContainer}
            }
        `,
    },
    logoSection: {
        css: css`
            margin-top: auto;
            padding-top: 95px;
            img {
                max-height: 40px;
                max-width: 100px;
            }
            ${xScreen("lte", "sm")} {
                display: none;
            }
        `,
    },
    copyrightWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
            margin-top: auto;
            gap: 8px 20px;

            & > div:last-child {
                display: flex;
                justify-content: flex-end;
            }

            & ${TypographyStyle}, & ${StyledButton} ${TypographyStyle} {
                font-size: 10px;
                letter-spacing: 0;
                line-height: 15px;
                margin-bottom: 0;
                color: ${color("common.white")};
            }

            & ${StyledButton} {
                &:not(:last-child) {
                    margin-right: 30px;
                }
                ${TypographyStyle}:hover {
                    color: ${color("common.ui5")};
                }
            }

            ${xScreen("lte", "sm")} {
                flex-direction: column-reverse;
                flex-wrap: nowrap;
                align-items: flex-start;
                justify-content: flex-start;
            }
        `,
    },
};

const styles = footerStyles;
const Footer = ({ id }: PageProps) => {
    const isMobileScreen = useAppSelector(state => (state as CustomAppState).xCommon.isMobileScreen);
    const infoSection = (
        <StyledWrapper {...styles.infoSection}>
            <Zone contentId={id} zoneName="Subscribe" />
            <Zone contentId={id} zoneName="SocialMedia" />
            <StyledWrapper {...styles.logoSection}>
                <Zone contentId={id} zoneName="Logo" />
            </StyledWrapper>
        </StyledWrapper>
    );

    const itemsSection = (
        <StyledWrapper {...styles.itemsSection}>
            <Zone contentId={id} zoneName="Items1" normalWidth />
            <Zone contentId={id} zoneName="Items2" normalWidth />
            <Zone contentId={id} zoneName="Items3" normalWidth />
            <Zone contentId={id} zoneName="ContactUs" normalWidth />
            <StyledWrapper {...styles.copyrightWrapper}>
                <Zone contentId={id} zoneName="Copyright" normalWidth />
                <Zone contentId={id} zoneName="CopyrightLinks" normalWidth />
            </StyledWrapper>
        </StyledWrapper>
    );

    // XNG-Changes: structure
    return (
        <Page as="footer" isFooter={true} {...styles.page}>
            {isMobileScreen ? (
                <>
                    <Page fullWidth={[true, true, true, true, true]} background="common.ui16">
                        {infoSection}
                    </Page>
                    <Page fullWidth={[true, true, true, true, true]} background="common.ui8">
                        {itemsSection}
                    </Page>
                </>
            ) : (
                <StyledWrapper {...styles.widgetGridContainer}>
                    {infoSection}
                    {itemsSection}
                </StyledWrapper>
            )}
        </Page>
    );
};

const pageModule: PageModule = {
    component: Footer,
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "Footer" instead of this constant.
 */
export const FooterContext = "Footer";
